import { useId, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { changePassword } from '../../features/auth/api'
import { apiErrorMessage } from '../../hooks/useApiError'
import { Modal } from '../ui/Modal'
import { Input } from '../ui/Input'
import { Button } from '../ui/Button'

export function ChangePasswordModal({ onClose }: { onClose: () => void }) {
  const formId = useId()
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [confirm, setConfirm] = useState('')
  const mutation = useMutation({
    mutationFn: changePassword,
    retry: false,
    gcTime: 0,
    onSuccess: () => {
      setCurrent('')
      setNext('')
      setConfirm('')
    },
  })

  const mismatch = confirm.length > 0 && next !== confirm
  const tooLong = new TextEncoder().encode(next).length > 72
  const samePassword = next.length > 0 && next === current
  const canSave =
    current.length > 0 &&
    Array.from(next).length >= 8 &&
    !tooLong &&
    !samePassword &&
    next === confirm

  function handleSave() {
    if (!canSave || mutation.isPending || mutation.isSuccess) return
    mutation.mutate({ current_password: current, new_password: next })
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="เปลี่ยนรหัสผ่าน"
      size="md"
      footer={
        mutation.isSuccess ? (
          <Button onClick={onClose}>ปิด</Button>
        ) : (
          <>
            <Button variant="glass-dark" onClick={onClose}>
              ยกเลิก
            </Button>
            <Button type="submit" form={formId} disabled={!canSave} loading={mutation.isPending}>
              {mutation.isPending ? 'กำลังบันทึก...' : 'บันทึก'}
            </Button>
          </>
        )
      }
    >
      {mutation.isSuccess ? (
        <p role="status" className="text-body text-emerald-700">
          เปลี่ยนรหัสผ่านสำเร็จ กรุณาใช้รหัสผ่านใหม่ในการเข้าสู่ระบบครั้งถัดไป
        </p>
      ) : (
        <form
          id={formId}
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault()
            handleSave()
          }}
        >
          <Input
            label="รหัสผ่านปัจจุบัน"
            type="password"
            autoComplete="current-password"
            name="current_password"
            maxLength={128}
            value={current}
            disabled={mutation.isPending}
            onChange={(event) => {
              setCurrent(event.target.value)
              mutation.reset()
            }}
          />
          <Input
            label="รหัสผ่านใหม่"
            type="password"
            autoComplete="new-password"
            name="new_password"
            placeholder="ขั้นต่ำ 8 ตัวอักษร"
            maxLength={128}
            value={next}
            disabled={mutation.isPending}
            error={
              tooLong
                ? 'รหัสผ่านยาวเกินไป กรุณาใช้รหัสผ่านที่สั้นลง'
                : samePassword
                  ? 'รหัสผ่านใหม่ต้องไม่เหมือนรหัสผ่านปัจจุบัน'
                  : undefined
            }
            onChange={(event) => {
              setNext(event.target.value)
              mutation.reset()
            }}
          />
          <Input
            label="ยืนยันรหัสผ่านใหม่"
            type="password"
            autoComplete="new-password"
            name="new_password_confirmation"
            maxLength={128}
            value={confirm}
            disabled={mutation.isPending}
            error={mismatch ? 'รหัสผ่านไม่ตรงกัน' : undefined}
            onChange={(event) => {
              setConfirm(event.target.value)
              mutation.reset()
            }}
          />
          {mutation.isError && (
            <p role="alert" className="text-body-sm text-rose-600">
              {apiErrorMessage(mutation.error, 'เปลี่ยนรหัสผ่านไม่สำเร็จ กรุณาลองอีกครั้ง')}
            </p>
          )}
        </form>
      )}
    </Modal>
  )
}
